<template>
    <label class="headline-modal">{{ $t("Profile name") }}</label>
    <input type="text" name="room-title" autocomplete="off" v-model="name" class="form-control" maxlength="80" @input="markDirty" />
    <div v-if="error_name" class="error-message">{{ error_name }}</div>
    <div class="pb-3" @drop="onDropImage">
        <img v-if="image" class="img-fluid rounded-image" :src="image" alt="user-img" />
        <img v-else class="img-fluid rounded-image" src="@/assets/user.png" alt="user-img" />
    </div>
    <div class="d-flex justify-content-start align-items-center flex-column mb-2">
        <input type="file" class="file-hidden file-image" @change="imageFileChanged" name="image-upload" />
        <div class="d-flex justify-content-start align-items-center w-100">
            <button
                type="button"
                v-if="!busyImage || imageUploadProgress <= 0"
                :disabled="busyImage"
                class="btn btn-primary btn-mr me-2"
                @click="selectImage"
            >
                <IconItem icon="mdi:upload" />
                <span class="text-responsive">{{ $t("Upload image") }}</span>
            </button>
            <button type="button" v-if="busyImage && imageUploadProgress > 0" disabled class="btn btn-primary btn-mr">
                {{ $t("Uploading image") }}... ({{ imageUploadProgress }}%)
            </button>
            <button type="button" :disabled="busyImage" class="btn btn-cancel" @click="deleteProfileImage">
                <IconItem icon="mdi:trash-can" /><span class="text-responsive">{{ $t("Delete image") }}</span>
            </button>
        </div>
        <div class="form-error d-flex justify-content-start align-items-center w-100">{{ errorImage }}</div>
    </div>
    <label class="headline-modal">{{ $t("Profile description") }}</label>
    <textarea
        name="room-description"
        autocomplete="off"
        v-model="profileDescription"
        class="form-control"
        maxlength="255"
        @input="markDirty"
    ></textarea>
    <label class="headline-modal">{{ $t("Website") }}</label>
    <input type="text" name="room-title" autocomplete="off" v-model="website" class="form-control" maxlength="80" @input="markDirty" />

    <div v-if="errorWebsite" class="error-message">{{ errorWebsite }}</div>
    <div class="btn-bottom-modal">
        <button type="submit" :disabled="busy || !dirty" class="btn btn-primary me-1 mb-2" @click="saveProfile">
            <IconItem icon="mdi:content-save" /><span class="text-responsive">{{ $t("Save changes") }}</span>
        </button>
        <div class="form-error" v-if="errorSave">{{ errorSave }}</div>
    </div>
    <div class="error-message" v-if="error">{{ error }}</div>
</template>

<script lang="ts">
import { getUniqueStringId } from "@/utils/unique-id";
import { useVModel } from "@/utils/v-model";
import { defineComponent, nextTick } from "vue";
import { Request } from "@asanrom/request-browser";
import { Timeouts } from "@/utils/timeout";
import { ApiProfile } from "@/api/api-group-profile";
import { AuthController } from "@/control/auth";

const IMAGE_UPLOAD_MAX_SIZE = 20 * 1024 * 1024;
export default defineComponent({
    name: "GeneralOptionsPage",
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            loadProfileRequestId: getUniqueStringId(),
            imageRequestId: getUniqueStringId(),
            saveRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            error_name: "",
            error: "",
            name: "",
            profileDescription: "",
            website: "",

            id: "",
            image: "",
            loading: false,
            errorloadProfile: "",

            dirty: false,

            busyImage: false,
            imageUploadProgress: 0,
            errorImage: "",

            errorSave: "",
            busy: false,

            errorWebsite: "",
            errorDescription: "",
        };
    },
    methods: {
        loadProfile: function () {
            Timeouts.Abort(this.loadProfileRequestId);
            Request.Abort(this.loadProfileRequestId);

            this.loading = true;
            this.errorloadProfile = "";

            if (!AuthController.isAuthenticated()) {
                this.$requireLogin();
                return;
            }

            Request.Pending(this.loadProfileRequestId, ApiProfile.GetProfile(AuthController.UID))
                .onSuccess((response) => {
                    this.loading = false;
                    this.id = response.id;
                    this.image = response.image;
                    this.name = response.name || response.username;
                    this.profileDescription = response.bio;
                    this.website = response.website;

                    this.dirty = false;
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        notFound: () => {
                            this.errorloadProfile = "not-found";
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadProfileRequestId, 1500, this.loadProfile.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    // Retry
                    Timeouts.Set(this.loadProfileRequestId, 1500, this.loadProfile.bind(this));
                });
        },

        markDirty: function () {
            this.dirty = true;
        },

        selectImage: function () {
            nextTick(() => {
                const fileElem = document.querySelector(".file-image") as HTMLInputElement;
                if (fileElem) {
                    fileElem.value = null;
                    fileElem.click();
                }
            });
        },

        imageFileChanged: function (e: Event) {
            const data = (e.target as any).files;
            if (data && data.length > 0) {
                const file = data[0] as File;
                this.$showImageSelectorModal({
                    file: file,
                    callback: (file) => {
                        this.uploadNewImage(file);
                    },
                });
            }
        },

        onDropImage: function (e: Event) {
            e.preventDefault();
            const data = (e as any).dataTransfer.files;
            if (data && data.length > 0) {
                const file = data[0] as File;
                this.uploadNewImage(file);
            }
        },

        uploadNewImage: function (file: File) {
            if (this.busyImage) {
                return;
            }

            if (file.size > IMAGE_UPLOAD_MAX_SIZE) {
                this.errorImage =
                    this.$t("Error") +
                    ": " +
                    this.$t("The image must not be bigger than $SIZE.").replace("$SIZE", this.renderSize(IMAGE_UPLOAD_MAX_SIZE));
                return;
            }

            this.errorImage = "";
            this.imageUploadProgress = 0;
            this.busyImage = true;

            Request.Pending(this.imageRequestId, ApiProfile.UpdateImage({ image: file }))
                .onSuccess((res) => {
                    this.busyImage = false;
                    this.image = res.url;
                    this.$showSnackBar(this.$t("Profile image successfully changed!"));
                    AuthController.CheckAuthStatus();
                })
                .onUploadProgress((loaded, total) => {
                    this.imageUploadProgress = Math.round((loaded * 100) / total);
                })
                .onRequestError((err, handleErr) => {
                    this.busyImage = false;
                    handleErr(err, {
                        badRequestNoImage: () => {
                            this.errorImage = this.$t("No image provided");
                        },
                        badRequestInvalidImage: () => {
                            this.errorImage = this.$t("Invalid image provided");
                        },
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequest: () => {
                            this.errorImage = this.$t("Invalid image provided");
                        },
                        serverError: () => {
                            this.errorImage = this.$t("Internal server error");
                        },
                        networkError: () => {
                            this.errorImage = this.$t("Could not connect to the server");
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.busyImage = false;
                    console.error(err);
                    this.errorImage = err.message;
                });
        },

        renderSize: function (bytes: number): string {
            if (bytes > 1024 * 1024 * 1024) {
                let gb = bytes / (1024 * 1024 * 1024);
                gb = Math.floor(gb * 100) / 100;
                return gb + " GB";
            } else if (bytes > 1024 * 1024) {
                let mb = bytes / (1024 * 1024);
                mb = Math.floor(mb * 100) / 100;
                return mb + " MB";
            } else if (bytes > 1024) {
                let kb = bytes / 1024;
                kb = Math.floor(kb * 100) / 100;
                return kb + " KB";
            } else {
                return bytes + " Bytes";
            }
        },

        deleteProfileImage: function () {
            this.$askUserConfirmation({
                title: this.$t("Delete profile image"),
                message: this.$t("Do you want to delete your current profile image?"),
                simpleDelete: true,
                callback: () => {
                    if (this.busyImage) {
                        return;
                    }

                    this.errorImage = "";
                    this.imageUploadProgress = 0;
                    this.busyImage = true;

                    Request.Pending(this.imageRequestId, ApiProfile.DeleteImage())
                        .onSuccess(() => {
                            this.busyImage = false;
                            this.$showSnackBar(this.$t("Profile image successfully deleted!"));
                            AuthController.CheckAuthStatus();
                        })
                        .onRequestError((err, handleErr) => {
                            this.busyImage = false;
                            handleErr(err, {
                                unauthorized: () => {
                                    this.$requireLogin();
                                },
                                serverError: () => {
                                    this.errorImage = this.$t("Error") + ": " + this.$t("Internal server error");
                                },
                                networkError: () => {
                                    this.errorImage = this.$t("Error") + ": " + this.$t("Could not connect to the server");
                                },
                            });
                        })
                        .onUnexpectedError((err) => {
                            this.busyImage = false;
                            console.error(err);
                            this.errorImage = err.message;
                        });
                },
            });
        },

        saveProfile: function () {
            if (this.busy) {
                return;
            }

            this.busy = true;

            const body = {
                name: this.name,
                bio: this.profileDescription,
                website: this.website,
            };

            Request.Pending(this.saveRequestId, ApiProfile.UpdateProfile(body))
                .onSuccess(() => {
                    this.busy = false;
                    this.dirty = false;
                    this.$showSnackBar(this.$t("Profile successfully updated!"));
                    AuthController.CheckAuthStatus();
                })
                .onRequestError((err, handleErr) => {
                    this.busy = false;
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequestInvalidName: () => {
                            this.error_name = this.$t("Invalid profile name");
                        },
                        badRequestInvalidBio: () => {
                            this.errorDescription = this.$t("Invalid profile description");
                        },
                        badRequestInvalidWebsite: () => {
                            this.errorWebsite = this.$t("Invalid website. Must be a valid URL.");
                        },
                        badRequestInvalidLocation: () => {
                            this.error = this.$t("Invalid website. Must be a valid URL.");
                        },
                        badRequest: () => {
                            this.errorSave = this.$t("Bad request");
                        },
                        serverError: () => {
                            this.errorSave = this.$t("Internal server error");
                        },
                        networkError: () => {
                            this.errorSave = this.$t("Could not connect to the server");
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.busy = false;
                    console.error(err);
                    this.errorSave = err.message;
                });
        },
    },
    mounted: function () {
        this.loadProfile();
    },
});
</script>

<style scoped>
@import "@/style/routes/configuration/user-config.css";
</style>
